import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Leaf, GraduationCap, Scale } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import AdBanner from "@/components/common/AdBanner";
import EventCard from "@/components/common/EventCard";
import SponsorCard from "@/components/common/SponsorCard";
import UnitCard from "@/components/common/UnitCard";
import { Spinner } from "@/components/common/Primitives";
import {
  isInitialLoading,
  useHomeSections,
  useAbout,
  useForums,
  useUpcomingEvents,
  useFeaturedUnits,
  useFeaturedSponsors,
  useMemberCount,
  useUnitCount,
} from "@/hooks/useSiteQueries";
import { withMemberCountInStats, withMemberCountInText } from "@/lib/memberCountDisplay";
import { formatUnitsSectionTitle } from "@/lib/unitCountDisplay";

const FORUM_ICONS = { Landmark, Leaf, GraduationCap };

export default function HomePage() {
  const homeQuery = useHomeSections();
  const aboutQuery = useAbout();
  const forumsQuery = useForums();
  const eventsQuery = useUpcomingEvents(3);
  const unitsQuery = useFeaturedUnits(4);
  const sponsorsQuery = useFeaturedSponsors(4);
  const { data: memberCount } = useMemberCount();
  const { data: unitCount } = useUnitCount();

  const queries = [homeQuery, aboutQuery, forumsQuery, eventsQuery, unitsQuery, sponsorsQuery];

  if (isInitialLoading(queries)) return <Spinner className="py-32" />;

  const home = homeQuery.data;
  const about = aboutQuery.data;
  const forums = forumsQuery.data;
  const events = eventsQuery.data;
  const units = unitsQuery.data;
  const sponsors = sponsorsQuery.data;
  const heroStats = withMemberCountInStats(about?.hero?.stats || [], memberCount);

  return (
    <div data-testid="home-page">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="container-bcs pt-16 sm:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="eyebrow mb-4">{home.hero.eyebrow}</p>
            <h1 className="heading-xl">
              {home.hero.title_line1}{" "}
              <span className="text-maroon italic">{home.hero.title_accent}</span>
            </h1>
            <div className="mt-6 h-0.5 w-16 bg-gold" />
            <p className="mt-6 text-base sm:text-lg text-ink-soft leading-relaxed max-w-2xl">
              {withMemberCountInText(home.hero.body, memberCount)}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/units" className="btn-primary" data-testid="hero-units-btn">
                Find your Unit / Parish <ArrowRight size={16} />
              </Link>
              <Link to="/our-work" className="btn-outline" data-testid="hero-work-btn">
                Explore forums
              </Link>
            </div>
            <dl className="mt-12 grid grid-cols-4 gap-6 max-w-md">
              {heroStats.map((s) => (
                <div key={s.label}>
                  <dt className="text-[11px] uppercase tracking-wider text-ink-muted">{s.label}</dt>
                  <dd className="mt-1 font-serif text-2xl sm:text-3xl font-semibold text-ink">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5 relative fade-up">
            <img
              src={home.hero.image_url}
              alt="Church interior"
              className="rounded-2xl w-full h-[420px] sm:h-[500px] object-cover shadow-card-hover"
            />
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-xl shadow-card-hover border border-gray-100 px-5 py-4 max-w-[230px]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold-dark font-semibold">Since 1989</p>
              <p className="mt-1 font-serif text-lg font-semibold text-ink leading-snug">
                Serving every deanery of the Archdiocese
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Hero banner zone ─────────────────────────────── */}
      <section className="container-bcs pb-16">
        <AdBanner placement="home_hero" />
      </section>

      {/* ── Forums ───────────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-24">
          <SectionHeading
            eyebrow={home.mission.eyebrow}
            title={home.mission.title}
            subtitle={home.mission.body}
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {forums.map((forum) => {
              const Icon = FORUM_ICONS[forum.icon] || Landmark;
              const isSocioCivic = forum.slug === "socio-civic-political";
              return (
                <Link
                  key={forum.slug}
                  to={`/our-work/${forum.slug}`}
                  className="relative overflow-hidden rounded-xl border border-gray-200 shadow-card hover:shadow-card-hover transition-shadow duration-300 group flex flex-col h-full min-h-[28rem]"
                  data-testid={`home-forum-card-${forum.slug}`}
                >
                  <img
                    src={forum.hero_image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-maroon/85 via-maroon/60 to-transparent" />
                  <div className="relative z-10 flex flex-col h-full p-7 text-white">
                    <span className="h-11 w-11 rounded-lg bg-white/15 flex items-center justify-center text-gold-light">
                      <Icon size={20} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-semibold text-white group-hover:text-gold-light transition-colors">
                      {forum.name}
                    </h3>
                    <div className="mt-2.5 flex-1 flex flex-col">
                      <p className="text-sm text-white/85 leading-relaxed">{forum.description}</p>
                      {isSocioCivic && (
                        <div className="mt-5 pt-5 border-t border-white/20 flex gap-3.5">
                          <span className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center text-gold-light shrink-0">
                            <Scale size={17} />
                          </span>
                          <div className="min-w-0">
                            <h4 className="font-serif text-base font-semibold text-white">{home.legal_aid.title}</h4>
                            <p className="mt-1.5 text-sm text-white/80 leading-relaxed">{home.legal_aid.body}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gold-light">
                      Visit forum <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Featured Units ───────────────────────────────── */}
      <section className="container-bcs py-20 sm:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHeading
            eyebrow="My Parish · My Unit"
            title={formatUnitsSectionTitle(unitCount)}
            subtitle="Every BCS unit is rooted in a parish — find yours, see its events and read its updates."
          />
          <Link to="/units" className="btn-outline shrink-0" data-testid="home-all-units-btn">
            Search all units <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {units.map((u) => (
            <UnitCard key={u.slug} unit={u} />
          ))}
        </div>
      </section>

      {/* ── Upcoming Events ──────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Mark Your Calendar"
              title="Upcoming events"
              subtitle="Conventions, drives, camps and celebrations across our units and forums."
            />
            <Link to="/events" className="btn-outline shrink-0" data-testid="home-all-events-btn">
              All events <ArrowRight size={15} />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {events.map((e) => (
              <EventCard key={e.slug} event={e} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Mid banner zone ──────────────────────────────── */}
      <section className="container-bcs pt-16">
        <AdBanner placement="home_mid" />
      </section>

      {/* ── Sponsors preview ─────────────────────────────── */}
      <section className="container-bcs py-20 sm:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHeading
            eyebrow="Sponsors & Well-Wishers"
            title="The supporters who stand with us"
            subtitle="Businesses, institutions and families who make our community mission possible."
          />
          <Link to="/sponsors" className="btn-outline shrink-0" data-testid="home-all-sponsors-btn">
            View all sponsors <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sponsors.map((s) => (
            <SponsorCard key={s.id} sponsor={s} />
          ))}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="bg-maroon">
        <div className="container-bcs py-20 text-center">
          <p className="eyebrow !text-gold-light mb-4">Join Us</p>
          <h2 className="heading-lg !text-white max-w-2xl mx-auto">{home.cta.title}</h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
            {withMemberCountInText(home.cta.body, memberCount)}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-secondary" data-testid="cta-contact-btn">
              Get in touch <ArrowRight size={16} />
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-maroon rounded-full px-6 py-3 text-sm font-medium transition-colors"
              data-testid="cta-membership-btn"
            >
              Why become a member?
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
